import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { glass } from "./portal";
import { useProject } from "@/lib/portal/useProject";
import { CreditCard, RefreshCcw, ArrowUpRight, XCircle } from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/portal/subscription")({
  component: SubscriptionPage,
});

const PLANS = [
  { key: "landing", name: "Sito Landing", price: "CHF 990 una tantum" },
  { key: "flex", name: "FlashWeb Flex", price: "CHF 59/mese" },
  { key: "premium", name: "Sito Premium", price: "CHF 1'790 una tantum" },
];

function SubscriptionPage() {
  const qc = useQueryClient();
  const { data: project } = useProject();
  const { data: sub } = useQuery({
    queryKey: ["portal", "sub"],
    queryFn: async () => (await supabase.from("subscriptions").select("*").limit(1).maybeSingle()).data,
    enabled: !!project,
  });
  const requestCancel = async () => {
    if (!sub) return;
    await supabase.from("subscriptions").update({ cancel_requested_at: new Date().toISOString(), status: "cancel_requested" }).eq("id", sub.id);
    toast.success("Richiesta di cancellazione inviata");
    qc.invalidateQueries({ queryKey: ["portal", "sub"] });
  };
  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Abbonamento</h1>
        <p className="text-white/60 mt-1">Piano attuale, rinnovo, metodo di pagamento.</p>
      </div>
      <div className={`${glass} p-6 relative overflow-hidden`}>
        <div className="absolute -top-16 -right-16 h-64 w-64 rounded-full bg-fuchsia-500/20 blur-3xl" />
        <div className="relative">
          <div className="text-xs uppercase tracking-widest text-white/40">Piano attivo</div>
          <div className="mt-2 flex items-baseline gap-3 flex-wrap">
            <div className="text-3xl font-semibold">{PLANS.find((p) => p.key === sub?.plan)?.name ?? "—"}</div>
            <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-300">{sub?.status ?? "—"}</span>
          </div>
          <div className="mt-1 text-white/70">{sub ? `${sub.currency} ${sub.price} / ${sub.billing_cycle}` : ""}</div>
          <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
            <div><div className="text-white/40 text-xs">Rinnovo</div><div className="font-medium">{sub?.renewal_date ? format(new Date(sub.renewal_date), "d MMM yyyy", { locale: it }) : "—"}</div></div>
            <div><div className="text-white/40 text-xs">Metodo di pagamento</div><div className="font-medium flex items-center gap-1.5"><CreditCard className="h-4 w-4" />{sub?.payment_method ?? "—"}</div></div>
          </div>
          <div className="mt-6 flex gap-2 flex-wrap">
            <button className="px-4 py-2 rounded-full bg-gradient-to-r from-fuchsia-500 to-cyan-400 text-black text-sm font-semibold flex items-center gap-1.5"><ArrowUpRight className="h-4 w-4" /> Upgrade piano</button>
            <button className="px-4 py-2 rounded-full bg-white/10 text-sm flex items-center gap-1.5"><RefreshCcw className="h-4 w-4" /> Cambia metodo</button>
            <button onClick={requestCancel} className="px-4 py-2 rounded-full bg-white/5 hover:bg-red-500/10 hover:text-red-300 text-white/60 text-sm flex items-center gap-1.5"><XCircle className="h-4 w-4" /> Richiedi cancellazione</button>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {PLANS.map((p) => (
          <div key={p.key} className={`${glass} p-5 ${sub?.plan === p.key ? "ring-1 ring-fuchsia-400/50" : ""}`}>
            <div className="font-semibold">{p.name}</div>
            <div className="text-sm text-white/60 mt-1">{p.price}</div>
            {sub?.plan === p.key ? <div className="mt-3 text-xs text-fuchsia-300">Piano attuale</div> : <button className="mt-3 text-xs text-white/70 hover:text-white underline">Passa a questo piano</button>}
          </div>
        ))}
      </div>
    </div>
  );
}
